import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { isFunction } from "lodash";

import {
  addQuickFilter,
  applyLeadFilters,
  QUICK_FILTERS,
  resetLeadFilters,
  setCampaignFilters,
  setUserFilters,
  setTeamFilters,
  setDateFilterType,
  setLeadDateRange,
  setQuickFilters,
  setOpportunityTypeFilters,
  setOpportunityStatusFilters,
  setOpportunitySourceFilters,
  setOpportunityTypeFilterCount,
  setOpportunityStatusFilterCount,
  setOpportunitySourceFilterCount,
} from "../../../redux/slices/leadSlice";
import FilterPanel from "../../commonComponents/FilterPanel/FilterPanel";
import QuickFiltersContent from "./filters/QuickFiltersContent";
import CampaignFiltersContent from "./filters/CampaignFiltersContent";
import UserFiltersContent from "./filters/UserFiltersContent";
import TeamFiltersContent from "./filters/TeamFiltersContent";
import DateRangeFiltersContent from "./filters/DateRangeFiltersContent";
import OpportunityTypeContent from "./filters/OpportunityTypeContent";
import OpportunityStatusContent from "./filters/OpportunityStatusContent";
import OpportunitySourceContent from "./filters/OpportunitySourceContent";
import SavedFiltersFooter from "./SavedFiltersFooter";

const ActivityCenterFilter = ({
  variables,
  setVariables,
  scrollListToTop,
  clearUrl = () => {},
  restrictRemoveDefaultFilter = false,
  handleVisibleChange,
  onClearAllFilters,
}) => {
  const dispatch = useDispatch();
  // Full filter object — serialized as-is when saving a preset filter.
  const allFilters = useSelector(state => state.lead.filters);
  const {
    quickFilters = [],
    campaignFilters = [],
    userFilters = [],
    teamFilters = [],
    dateFilterType = "",
    dateRange = [],
    opportunityTypeFilterCount = 0,
    opportunityStatusFilterCount = 0,
    opportunitySourceFilterCount = 0,
  } = allFilters;

  // Date tab shows a "1" chip when ANY date selection is committed —
  // either a preset (filterType set) or a complete custom range.
  const dateFilterCount =
    dateFilterType || (Array.isArray(dateRange) && dateRange.length === 2) ? 1 : 0;

  // Gate the "Save filter" action — only offer to save when the user has
  // actually applied something.
  const hasActiveFilters =
    quickFilters.length > 0 ||
    campaignFilters.length > 0 ||
    userFilters.length > 0 ||
    teamFilters.length > 0 ||
    dateFilterCount > 0 ||
    opportunityTypeFilterCount > 0 ||
    opportunityStatusFilterCount > 0 ||
    opportunitySourceFilterCount > 0;

  const handleScrollToTop = () => {
    if (isFunction(scrollListToTop)) {
      scrollListToTop();
    }
  };

  const handleClearAll = () => {
    setVariables?.({ ...variables, page: 1, pageSize: 25, campaignIds: [] });
    dispatch(resetLeadFilters());
    dispatch(setOpportunityTypeFilters([]));
    dispatch(setOpportunityStatusFilters([]));
    dispatch(setOpportunitySourceFilters([]));
    if (restrictRemoveDefaultFilter) {
      dispatch(addQuickFilter(QUICK_FILTERS.MY_CONVERSATIONS));
    }
    handleScrollToTop();
    clearUrl?.();
    onClearAllFilters?.();
  };

  // Apply a saved/preset filter: replace the whole redux filter set with the
  // parsed payload, then sync `campaignIds` (which the list query reads from
  // `variables`, not redux) and reset paging so results start from page 1.
  const handleApplySavedFilter = parsed => {
    dispatch(applyLeadFilters(parsed));
    setVariables?.({
      ...variables,
      page: 1,
      pageSize: 25,
      campaignIds: parsed?.campaignFilters || [],
    });
    handleScrollToTop();
  };

  const sharedProps = {
    variables,
    setVariables,
    scrollListToTop,
    clearUrl,
    onFilterChange: onClearAllFilters,
  };

  const categories = [
    {
      key: "quickFilters",
      label: "Quick filters",
      count: quickFilters.length,
      searchable: false,
      onClear: () => {
        dispatch(setQuickFilters([]));
        if (restrictRemoveDefaultFilter) {
          dispatch(addQuickFilter(QUICK_FILTERS.MY_CONVERSATIONS));
        }
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <QuickFiltersContent
          {...sharedProps}
          restrictRemoveDefaultFilter={restrictRemoveDefaultFilter}
          search={search}
          onClear={onClear}
        />
      ),
    },
    {
      key: "campaigns",
      label: "Campaigns",
      count: campaignFilters.length,
      searchPlaceholder: "Search campaigns",
      onClear: () => {
        dispatch(setCampaignFilters([]));
        setVariables?.({ ...variables, page: 1, pageSize: 25, campaignIds: [] });
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <CampaignFiltersContent {...sharedProps} search={search} onClear={onClear} />
      ),
    },
    {
      key: "users",
      label: "Users",
      count: userFilters.length,
      searchPlaceholder: "Search users",
      onClear: () => {
        dispatch(setUserFilters([]));
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <UserFiltersContent {...sharedProps} search={search} onClear={onClear} />
      ),
    },
    {
      key: "teams",
      label: "Teams",
      count: teamFilters.length,
      searchPlaceholder: "Search teams",
      onClear: () => {
        dispatch(setTeamFilters([]));
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <TeamFiltersContent {...sharedProps} search={search} onClear={onClear} />
      ),
    },
    {
      key: "dateRange",
      label: "Date range",
      count: dateFilterCount,
      // Pure picker UI — no list to search through.
      searchable: false,
      onClear: () => {
        dispatch(setDateFilterType(""));
        dispatch(setLeadDateRange([]));
        handleScrollToTop();
      },
      render: (search, onClear) => <DateRangeFiltersContent {...sharedProps} onClear={onClear} />,
    },
    {
      key: "opportunityType",
      label: "Opportunity type",
      count: opportunityTypeFilterCount,
      searchPlaceholder: "Search opportunity types",
      onClear: () => {
        dispatch(setOpportunityTypeFilters([]));
        dispatch(setOpportunityTypeFilterCount(0));
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <OpportunityTypeContent
          search={search}
          scrollListToTop={scrollListToTop}
          onClear={onClear}
        />
      ),
    },
    {
      key: "opportunityStatus",
      label: "Opportunity status",
      count: opportunityStatusFilterCount,
      searchPlaceholder: "Search opportunity statuses",
      onClear: () => {
        dispatch(setOpportunityStatusFilters([]));
        dispatch(setOpportunityStatusFilterCount(0));
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <OpportunityStatusContent
          search={search}
          scrollListToTop={scrollListToTop}
          onClear={onClear}
        />
      ),
    },
    {
      key: "opportunitySource",
      label: "Opportunity source",
      count: opportunitySourceFilterCount,
      searchPlaceholder: "Search opportunity sources",
      onClear: () => {
        dispatch(setOpportunitySourceFilters([]));
        dispatch(setOpportunitySourceFilterCount(0));
        handleScrollToTop();
      },
      render: (search, onClear) => (
        <OpportunitySourceContent
          search={search}
          scrollListToTop={scrollListToTop}
          onClear={onClear}
        />
      ),
    },
  ];

  return (
    <FilterPanel
      categories={categories}
      onClearAll={handleClearAll}
      onClose={handleVisibleChange}
      width={650}
      footer={
        <SavedFiltersFooter
          filters={allFilters}
          hasActiveFilters={hasActiveFilters}
          onApply={handleApplySavedFilter}
        />
      }
    />
  );
};

export default ActivityCenterFilter;
